import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { useFirestore, useUser } from 'reactfire';
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useFormik } from 'formik';

import {
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { initialSuggestions } from '../Chat';

const changeRequestSchema = yup.object().shape({
  requestType: yup.string().required('Обов’язкове поле'),
  title: yup.string().required('Обов’язкове поле'),
  description: yup.string().required('Обов’язкове поле'),
});

interface ChangeRequestFormProps {
  code: string;
  onSuccess: () => void;
}

const ChangeRequestForm = ({ code, onSuccess }: ChangeRequestFormProps) => {
  const { sessionId } = useParams();
  const firestore = useFirestore();
  const { data: user } = useUser();

  const formik = useFormik({
    initialValues: {
      requestType: '',
      title: '',
      description: '',
    },
    validationSchema: changeRequestSchema,
    async onSubmit(
      { requestType, title, description },
      { setErrors, setSubmitting },
    ) {
      try {
        if (!sessionId || !code || !user) return;

        const changeRequestsCollection = collection(
          firestore,
          'changeRequests',
        );
        const changeRequestsQuery = query(
          changeRequestsCollection,
          where('userId', '==', user.uid),
        );

        const querySnapshot = await getDocs(changeRequestsQuery);

        const existingSession = querySnapshot.docs.find(
          (doc) => doc.id === sessionId,
        );

        if (existingSession) {
          return setErrors({ title: 'Запит на доопрацювання вже існує' });
        }

        const changeRequestRef = doc(firestore, 'changeRequests', sessionId);

        const randomRecordId = Math.floor(1000 + Math.random() * 9000);

        await setDoc(changeRequestRef, {
          userId: user.uid,
          recordId: randomRecordId,
          requestType,
          rowsCount: null,
          paymentAmount: null,
          paymentDate: null,
          comment: '',
          title,
          description,
          code,
          status: 'pending',
          docs: [],
          createdAt: serverTimestamp(),
        });

        const conversationRef = doc(
          firestore,
          `chats/${user.uid}/conversations/${sessionId}`,
        );

        await updateDoc(conversationRef, {
          recordId: randomRecordId,
          requestDate: serverTimestamp(),
        });

        onSuccess();
      } catch (error) {
        setErrors({ title: 'Сталася помилка' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogContent>
        <DialogContentText>
          Заповніть поля "Тип запиту", "Заголовок" та "Опис" для відправки
          запиту на доопрацювання
        </DialogContentText>
        <FormControl
          fullWidth
          margin="dense"
          variant="standard"
          error={
            formik.touched.requestType && Boolean(formik.errors.requestType)
          }
        >
          <InputLabel id="requestType-label">Тип запиту</InputLabel>
          <Select
            labelId="requestType-label"
            id="requestType"
            name="requestType"
            value={formik.values.requestType}
            onChange={formik.handleChange}
          >
            {initialSuggestions.map((suggestion, index) => (
              <MenuItem value={suggestion} key={index}>
                {suggestion}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          id="title"
          name="title"
          label="Заголовок"
          margin="dense"
          variant="standard"
          value={formik.values.title}
          onChange={formik.handleChange}
          error={formik.touched.title && Boolean(formik.errors.title)}
          helperText={formik.touched.title && formik.errors.title}
          fullWidth
          autoFocus
        />
        <TextField
          id="description"
          name="description"
          label="Опис"
          margin="dense"
          variant="standard"
          maxRows={10}
          value={formik.values.description}
          onChange={formik.handleChange}
          error={
            formik.touched.description && Boolean(formik.errors.description)
          }
          helperText={formik.touched.description && formik.errors.description}
          multiline
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button type="submit" disabled={formik.isSubmitting}>
          Відправити
        </Button>
      </DialogActions>
    </form>
  );
};

export default ChangeRequestForm;
